import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import supabase from 'lib/supabase-browser'
import { apiRequest } from '../util/util'
import { useRouter } from 'next/router'
import { toast } from 'react-hot-toast'
import { useProjectStore } from 'store/useProjectStore'

export const useCustomer = ({ userId }: { userId: string | null }) => {
  return useQuery(
    ['customer', { userId }],
    async () => {
      if (!userId) return null
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('id', userId)
        .maybeSingle()
      if (error) {
        return null
      }
      return data
    },
    {
      enabled: !!userId,
    }
  )
}

export function useCustomerWithInvoices({ userId }: { userId: string | null }) {
  return useQuery(
    ['customer', { userId }],
    async () => {
      if (!userId) return null
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('id', userId)
        .single()
      if (error) throw error

      // make a request to the stripe-get-invoices endpoint
      const invoices = await apiRequest('stripe-get-invoices', 'POST', {
        customerId: data.stripeCustomerId,
      })

      return { ...data, invoices: invoices.data }
    },
    {
      enabled: !!userId,
      keepPreviousData: true,
    }
  )
}

export const useCreateBillingSession = () => {
  const router = useRouter()
  return useMutation(async ({ userId }: { userId: string }) => {
    const response = await apiRequest('stripe-create-billing-session', 'POST', {
      userId,
    })
    router.push(response.url)
  })
}

export const useUpgradeTrial = () => {
  const queryClient = useQueryClient()
  const { setShowUpgradeTrialModal, setShowPricingModal, setShowConfetti } =
    useProjectStore()
  return useMutation(
    async ({
      subscriptionId,
      priceId,
    }: {
      subscriptionId: string
      priceId?: string
    }) => {
      await apiRequest('upgrade-trial', 'POST', { subscriptionId, priceId })
    },
    {
      onSuccess: () => {
        setShowConfetti(true)
        setShowUpgradeTrialModal(false)
        setShowPricingModal(false)
        toast.success('Your trial has been upgraded. Enjoy!')
        setTimeout(() => {
          queryClient.invalidateQueries()
        }, 3000)
      },
    }
  )
}

export const usePreviewUpgradePrice = () => {
  return useMutation(
    async ({
      subscriptionId,
      priceId,
    }: {
      subscriptionId: string
      priceId?: string
    }) => {
      const response = await apiRequest(
        'stripe-preview-upgrade-price',
        'POST',
        {
          subscriptionId,
          priceId,
        }
      )
      return response
    }
  )
}
